import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaHome,
  FaPlus,
  FaUser,
  FaCrown,
  FaSignOutAlt,
  FaFolder,
  FaMagic,
  FaShoppingCart,
  FaEllipsisH,
  FaTimes,
  FaRocket,
} from 'react-icons/fa';

import { MOBILE_NAV_HEIGHT } from '../constants/layout';
import { shellAwarePath, isInsideRoleShell } from '../shared/ui/roleShell';

export const MOBILE_BOTTOM_NAV_OFFSET = `calc(${MOBILE_NAV_HEIGHT} + env(safe-area-inset-bottom, 0px))`;

const MobileBottomNav = ({ isLoggedIn, admin, onLogout, onCreateCoursePress }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  const isActive = (path) => path && (location.pathname === path || location.pathname === shellAwarePath(path, location.pathname));
  const isCreateFlow =
    location.pathname === '/create-choice' ||
    location.pathname === '/genui' ||
    location.pathname.endsWith('/create-choice') ||
    location.pathname.endsWith('/genui');

  if (!isLoggedIn) return null;

  // Lesson player: keep the thumb zone free for learning CTAs
  if (location.pathname.includes('/course')) return null;
  // Role shells have their own nav
  if (isInsideRoleShell(location.pathname)) return null;

  const primaryItems = [
    { key: 'home', label: t('header.nav.homeShort'), path: '/home', icon: FaHome },
    { key: 'projects', label: t('home.dashboard.projects'), path: '/projects', icon: FaFolder, badge: t('header.mobile.badgeNew') },
    { key: 'create', label: t('header.nav.createCourseShort'), path: '/create-choice', icon: FaPlus, isFab: true },
    { key: 'genui', label: t('header.aiStudio.menu'), path: '/genui', icon: FaMagic, badge: t('header.mobile.badgeAi') },
    { key: 'more', label: t('header.nav.more'), path: null, icon: FaEllipsisH, action: 'more' },
  ];

  const moreItems = [
    { key: 'marketplace', label: t('header.aiStudio.marketplace'), path: '/marketplace', icon: FaShoppingCart },
    { key: 'features', label: t('header.nav.features'), path: '/features', icon: FaRocket },
    { key: 'profile', label: t('header.nav.profile'), path: '/learn/profile', icon: FaUser },
    ...(admin
      ? [{ key: 'admin', label: t('header.nav.admin'), path: '/admin/dashboard', icon: FaCrown }]
      : []),
    { key: 'logout', label: t('header.auth.logout'), path: null, icon: FaSignOutAlt, action: 'logout', danger: true },
  ];

  const handleNav = (item) => {
    if (item.action === 'more') {
      setMoreOpen(true);
      return;
    }
    if (item.action === 'logout') {
      setMoreOpen(false);
      onLogout?.();
      return;
    }
    if (item.key === 'create' && typeof onCreateCoursePress === 'function') {
      onCreateCoursePress();
      return;
    }
    if (item.path) navigate(shellAwarePath(item.path, location.pathname));
  };

  const isMoreActive = moreItems.some((item) => item.path && isActive(item.path));

  return (
    <>
      <AnimatePresence>
        {moreOpen && (
          <>
            <motion.button
              type="button"
              aria-label={t('header.mobile.closeMenuAria')}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[60] bg-black/40 lg:hidden"
              onClick={() => setMoreOpen(false)}
            />
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 320 }}
              className="fixed inset-x-0 bottom-0 z-[70] lg:hidden rounded-t-2xl bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 shadow-2xl pb-[env(safe-area-inset-bottom,0px)]"
              role="dialog"
              aria-modal="true"
              aria-label={t('header.mobile.moreNavAria')}
            >
              <div className="flex items-center justify-between px-4 pt-4 pb-2">
                <p className="text-sm font-semibold text-slate-900 dark:text-white">{t('header.mobile.moreTitle')}</p>
                <button
                  type="button"
                  onClick={() => setMoreOpen(false)}
                  className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                  aria-label={t('header.mobile.closeAria')}
                >
                  <FaTimes size={16} />
                </button>
              </div>
              <div className="px-3 pb-4 grid grid-cols-2 gap-2">
                {moreItems.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => handleNav(item)}
                    className={`min-h-[52px] flex items-center gap-3 px-4 py-3 rounded-xl text-start text-sm font-semibold transition-colors ${
                      item.danger
                        ? 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-950/50'
                        : isActive(item.path)
                          ? 'text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/40'
                          : 'text-slate-700 dark:text-slate-200 bg-slate-50 dark:bg-slate-800/80 hover:bg-slate-100 dark:hover:bg-slate-800'
                    }`}
                  >
                    <item.icon className="shrink-0 w-5 h-5" aria-hidden />
                    <span className="truncate">{item.label}</span>
                  </button>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <nav
        aria-label={t('header.mobile.primaryNavAria')}
        className="fixed bottom-0 start-0 end-0 lg:hidden z-50 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 shadow-[0_-4px_24px_rgba(15,23,42,0.08)] pb-[env(safe-area-inset-bottom,0px)]"
      >
        <div className="grid grid-cols-5 items-end max-w-lg mx-auto px-1 pt-1">
          {primaryItems.map((item) => {
            const active =
              item.key === 'more'
                ? isMoreActive || moreOpen
                : item.key === 'create'
                  ? isCreateFlow
                  : isActive(item.path);

            if (item.isFab) {
              return (
                <div key={item.key} className="flex flex-col items-center -mt-3 pb-1">
                  <motion.button
                    type="button"
                    whileTap={{ scale: 0.92 }}
                    onClick={() => handleNav(item)}
                    aria-label={item.label}
                    className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/25 transition-colors ${
                      isCreateFlow
                        ? 'bg-blue-600 text-white ring-2 ring-blue-300 dark:ring-blue-700'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    <FaPlus className="w-6 h-6" aria-hidden />
                  </motion.button>
                  <span className="mt-1 text-[10px] font-semibold text-blue-600 dark:text-blue-400 leading-none text-center px-0.5 truncate max-w-[4.5rem]">
                    {t('header.mobile.createShort')}
                  </span>
                </div>
              );
            }

            return (
              <motion.button
                key={item.key}
                type="button"
                whileTap={{ scale: 0.95 }}
                onClick={() => handleNav(item)}
                aria-label={item.label}
                aria-current={active ? 'page' : undefined}
                className={`relative flex flex-col items-center justify-center min-h-[56px] py-2 px-1 rounded-xl transition-colors ${
                  active
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-slate-600 dark:text-slate-400 active:bg-slate-100 dark:active:bg-slate-800'
                }`}
              >
                <div className="relative">
                  <item.icon className="w-5 h-5 mb-0.5" aria-hidden />
                  {item.badge && (
                    <span
                      className={`absolute -top-1 -right-2 px-1 py-px text-white font-bold rounded-full leading-none ${
                        item.key === 'projects' ? 'bg-blue-500' : 'bg-violet-500'
                      }`}
                      style={{ fontSize: '8px' }}
                    >
                      {item.badge}
                    </span>
                  )}
                </div>
                <span className={`text-[10px] leading-tight text-center truncate max-w-[4.25rem] ${active ? 'font-bold' : 'font-medium'}`}>
                  {item.label}
                </span>
                {active && item.key !== 'more' && (
                  <span className="absolute bottom-1 w-5 h-0.5 rounded-full bg-blue-600 dark:bg-blue-400" aria-hidden />
                )}
              </motion.button>
            );
          })}
        </div>
      </nav>
    </>
  );
};

export default MobileBottomNav;
